/**
 * Team logo filter strip: desktop scroll affordance.
 *
 * Progressive enhancement over the horizontally scrolling <nav> rendered by
 * snippets/collection-team-logo-filters.liquid. Sets `data-can-left` /
 * `data-can-right` on the host while the row overflows in that direction; the
 * snippet's Tailwind classes show the arrows and edge fades from those
 * attributes (desktop only). The arrows scroll the row by most of a viewport.
 *
 * Defined as a custom element because the strip lives inside
 * #ProductGridContainer and is re-inserted via innerHTML on every facet
 * change, so connectedCallback re-runs without any extra wiring.
 */
class TeamLogoStrip extends HTMLElement {
	connectedCallback() {
		this.scroller = this.querySelector("[data-team-strip-scroller]")
		if (!this.scroller) {
			// Parser-created elements can connect before their children exist when
			// the definition is already registered; try once more after parsing.
			if (!this.retried) {
				this.retried = true
				requestAnimationFrame(() => {
					if (this.isConnected) this.connectedCallback()
				})
			}
			return
		}

		this.update = this.update.bind(this)
		this.scheduleUpdate = this.scheduleUpdate.bind(this)
		this.onArrowClick = this.onArrowClick.bind(this)

		this.scroller.addEventListener("scroll", this.scheduleUpdate, { passive: true })
		this.addEventListener("click", this.onArrowClick)

		if ("ResizeObserver" in window) {
			this.resizeObserver = new ResizeObserver(this.scheduleUpdate)
			this.resizeObserver.observe(this.scroller)
			const list = this.scroller.firstElementChild
			if (list) this.resizeObserver.observe(list)
		} else {
			window.addEventListener("resize", this.scheduleUpdate)
		}

		this.update()
	}

	disconnectedCallback() {
		if (this.scroller) this.scroller.removeEventListener("scroll", this.scheduleUpdate)
		this.removeEventListener("click", this.onArrowClick)
		if (this.resizeObserver) this.resizeObserver.disconnect()
		window.removeEventListener("resize", this.scheduleUpdate)
		if (this.frame) cancelAnimationFrame(this.frame)
	}

	scheduleUpdate() {
		if (this.frame) return
		this.frame = requestAnimationFrame(() => {
			this.frame = null
			this.update()
		})
	}

	update() {
		const { scrollLeft, scrollWidth, clientWidth } = this.scroller
		// 1px tolerance: fractional scroll positions never land exactly on the end.
		const canLeft = scrollLeft > 1
		const canRight = scrollLeft + clientWidth < scrollWidth - 1
		this.toggleAttribute("data-can-left", canLeft)
		this.toggleAttribute("data-can-right", canRight)
	}

	onArrowClick(event) {
		const prev = event.target.closest("[data-team-strip-prev]")
		const next = event.target.closest("[data-team-strip-next]")
		if (!prev && !next) return
		event.preventDefault()
		const step = Math.max(this.scroller.clientWidth * 0.8, 96)
		this.scroller.scrollBy({ left: prev ? -step : step })
	}
}

if ("customElements" in window && !customElements.get("team-logo-strip")) {
	customElements.define("team-logo-strip", TeamLogoStrip)
}
